import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import type { ChangeContractMeterPayload } from "@/lib/api";

type CambioMedidorModalProps = {
  contract: {
    contractNumber: string;
    meterSerialNumber: string;
    meterType: string;
    meterBrand?: string;
    meterSealNumber?: string;
    customerName: string;
    address: string;
    sector: string;
  };
  lastReading: {
    currentReading: number;
    readAt: string | null;
  } | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (payload: ChangeContractMeterPayload) => Promise<void>;
};

type FormState = {
  installedAt: string;
  oldMeterReadingDate: string;
  oldMeterLastReading: string;
  brand: string;
  serialNumber: string;
  manufacturingYear: string;
  meterType: string;
  sealNumber: string;
  initialReading: string;
  chargeToCustomer: boolean;
  chargeType: "smart" | "mechanical" | "custom";
  chargeAmount: string;
  reason: string;
};

export default function CambioMedidorModal({
  contract,
  lastReading,
  saving,
  onClose,
  onSubmit,
}: CambioMedidorModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(() => buildInitialState(lastReading, contract));

  useEffect(() => {
    setForm(buildInitialState(lastReading, contract));
    setError(null);
  }, [contract, lastReading]);

  const oldMeterLastReadingNumber = Number(form.oldMeterLastReading || 0);
  const initialReadingNumber = Number(form.initialReading || 0);
  const removedConsumption = Math.max(0, oldMeterLastReadingNumber - (lastReading?.currentReading ?? 0));

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    if (!form.installedAt) return setError("La fecha de instalación es obligatoria.");
    if (!form.oldMeterReadingDate) return setError("La fecha de lectura del medidor retirado es obligatoria.");
    if (!form.serialNumber.trim()) return setError("El número del nuevo medidor es obligatorio.");
    if (!form.meterType.trim()) return setError("El tipo de medidor es obligatorio.");
    if (!Number.isFinite(oldMeterLastReadingNumber)) return setError("La lectura del medidor retirado no es válida.");
    if (!Number.isFinite(initialReadingNumber)) return setError("La lectura inicial del medidor nuevo no es válida.");
    if (form.chargeToCustomer && form.chargeAmount.trim() && !Number.isFinite(Number(form.chargeAmount))) {
      return setError("El costo del medidor no es válido.");
    }

    await onSubmit({
      installedAt: form.installedAt,
      oldMeterReadingDate: form.oldMeterReadingDate,
      oldMeterLastReading: oldMeterLastReadingNumber,
      initialReading: initialReadingNumber,
      chargeToCustomer: form.chargeToCustomer,
      chargeType: form.chargeToCustomer ? form.chargeType : undefined,
      chargeAmount: form.chargeToCustomer && form.chargeAmount.trim() ? Number(form.chargeAmount) : undefined,
      reason: form.reason.trim() || undefined,
      newMeter: {
        brand: form.brand.trim() || undefined,
        meterType: form.meterType.trim(),
        serialNumber: form.serialNumber.trim(),
        sealNumber: form.sealNumber.trim() || undefined,
        manufacturingYear: form.manufacturingYear.trim() ? Number(form.manufacturingYear) : undefined,
        status: "active",
      },
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/45" onClick={saving ? undefined : onClose} />
      <div className="relative z-10 w-full max-w-5xl rounded-2xl bg-white shadow-2xl">
        <form onSubmit={handleSubmit}>
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
            <div>
              <h3 className="text-xl font-semibold text-slate-900">Cambio de medidor</h3>
              <p className="mt-1 text-sm text-slate-500">
                {contract.customerName} · {contract.contractNumber} · {contract.address}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cerrar
            </button>
          </div>

          <div className="grid gap-6 px-6 py-5 lg:grid-cols-2">
            <section className="rounded-2xl border border-amber-200 bg-amber-50/30 p-5">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-amber-700">Medidor retirado</h4>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Field label="Medidor actual">
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                    {contract.meterSerialNumber || "-"} · {contract.meterType || "-"}
                  </div>
                </Field>
                <Field label="Marca actual">
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                    {contract.meterBrand || "-"}
                  </div>
                </Field>
                <Field label="Última lectura registrada">
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                    {lastReading ? `${lastReading.currentReading} m³` : "Sin lecturas"}
                  </div>
                </Field>
                <Field label="Fecha última lectura">
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                    {lastReading?.readAt ? formatDate(lastReading.readAt) : "-"}
                  </div>
                </Field>
                <Field label="Lectura final del medidor retirado *">
                  <input
                    value={form.oldMeterLastReading}
                    onChange={(event) => setForm((prev) => ({ ...prev, oldMeterLastReading: event.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-amber-400"
                    inputMode="decimal"
                    placeholder="0"
                  />
                </Field>
                <Field label="Fecha de lectura *">
                  <input
                    type="date"
                    value={form.oldMeterReadingDate}
                    onChange={(event) => setForm((prev) => ({ ...prev, oldMeterReadingDate: event.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-amber-400"
                  />
                </Field>
              </div>

              <div className="mt-4 rounded-xl border border-amber-200 bg-white/90 px-4 py-3 text-sm text-slate-700">
                <div className="font-medium text-slate-800">Consumo previo al retiro: {removedConsumption.toLocaleString("es-CL")} m³</div>
                <div className="mt-1 text-xs text-slate-500">
                  Este consumo se sumará al cierre del período cuando la primera lectura del medidor nuevo quede dentro del mismo período.
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-sky-200 bg-sky-50/30 p-5">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-sky-700">Medidor instalado</h4>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Field label="Fecha de instalación *">
                  <input
                    type="date"
                    value={form.installedAt}
                    onChange={(event) => setForm((prev) => ({ ...prev, installedAt: event.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400"
                  />
                </Field>
                <Field label="Sector">
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">{contract.sector || "-"}</div>
                </Field>
                <Field label="Marca">
                  <input
                    value={form.brand}
                    onChange={(event) => setForm((prev) => ({ ...prev, brand: event.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400"
                    placeholder="Ej. Itron"
                  />
                </Field>
                <Field label="Número medidor *">
                  <input
                    value={form.serialNumber}
                    onChange={(event) => setForm((prev) => ({ ...prev, serialNumber: event.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400"
                    placeholder="Serie"
                  />
                </Field>
                <Field label="Año">
                  <input
                    value={form.manufacturingYear}
                    onChange={(event) => setForm((prev) => ({ ...prev, manufacturingYear: event.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400"
                    inputMode="numeric"
                    placeholder="2026"
                  />
                </Field>
                <Field label="Tipo *">
                  <select
                    value={form.meterType}
                    onChange={(event) => setForm((prev) => ({ ...prev, meterType: event.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400"
                  >
                    <option value="">Selecciona tipo</option>
                    <option value="Mecanico">Mecánico</option>
                    <option value="Inteligente">Inteligente</option>
                  </select>
                </Field>
                <Field label="N° sello">
                  <input
                    value={form.sealNumber}
                    onChange={(event) => setForm((prev) => ({ ...prev, sealNumber: event.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400"
                    placeholder="Opcional"
                  />
                </Field>
                <Field label="Lectura inicial *">
                  <input
                    value={form.initialReading}
                    onChange={(event) => setForm((prev) => ({ ...prev, initialReading: event.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400"
                    inputMode="decimal"
                    placeholder="0"
                  />
                </Field>
              </div>

              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                La próxima lectura tomará como base {initialReadingNumber.toLocaleString("es-CL")} m³ para el nuevo medidor.
              </div>
            </section>
          </div>

          <div className="grid gap-6 border-t border-slate-100 px-6 py-5 lg:grid-cols-[1.1fr_0.9fr]">
            <section className="space-y-4">
              <label className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.chargeToCustomer}
                  onChange={(event) => setForm((prev) => ({ ...prev, chargeToCustomer: event.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                Cobrar este medidor al usuario
              </label>

              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Tipo de cobro">
                  <select
                    value={form.chargeType}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        chargeType: event.target.value as FormState["chargeType"],
                      }))
                    }
                    disabled={!form.chargeToCustomer}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-400 disabled:cursor-not-allowed disabled:bg-slate-100"
                  >
                    <option value="custom">Personalizado</option>
                    <option value="smart">Medidor inteligente</option>
                    <option value="mechanical">Medidor mecánico</option>
                  </select>
                </Field>
                <Field label="Costo del medidor">
                  <input
                    value={form.chargeAmount}
                    onChange={(event) => setForm((prev) => ({ ...prev, chargeAmount: event.target.value }))}
                    disabled={!form.chargeToCustomer}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-400 disabled:cursor-not-allowed disabled:bg-slate-100"
                    inputMode="decimal"
                    placeholder="Opcional"
                  />
                </Field>
              </div>
            </section>

            <Field label="Observación">
              <textarea
                value={form.reason}
                onChange={(event) => setForm((prev) => ({ ...prev, reason: event.target.value }))}
                rows={4}
                maxLength={500}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-400"
                placeholder="Motivo del cambio, estado del medidor retirado u observaciones relevantes."
              />
            </Field>
          </div>

          {error ? <div className="px-6 pb-2 text-sm text-rose-600">{error}</div> : null}

          <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Guardando..." : "Guardar cambio"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function buildInitialState(
  lastReading: { currentReading: number; readAt: string | null } | null,
  contract: CambioMedidorModalProps["contract"],
): FormState {
  const today = new Date().toISOString().slice(0, 10);
  return {
    installedAt: today,
    oldMeterReadingDate: lastReading?.readAt?.slice(0, 10) ?? today,
    oldMeterLastReading: lastReading ? String(lastReading.currentReading) : "",
    brand: "",
    serialNumber: "",
    manufacturingYear: "",
    meterType: contract.meterType === "-" ? "" : contract.meterType,
    sealNumber: "",
    initialReading: "0",
    chargeToCustomer: false,
    chargeType: contract.meterType.toLowerCase().includes("intel") ? "smart" : "mechanical",
    chargeAmount: "",
    reason: "",
  };
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("es-CL");
}
